import axios from "axios";

const getBaseUrl = () => {
  return process.env.NODE_ENV === "development"
    ? "https://staging-tables-split-92ccb043d150.herokuapp.com/"
    : "https://staging-tables-split-92ccb043d150.herokuapp.com/";
};

// const getBaseUrl = () => {
//   return "http://localhost:4000/";
//   // return process.env.REACT_APP_API_URL;
// };

// // Usage
const url = getBaseUrl();

const apiInstance = axios.create({
  baseURL: url,
  timeout: 60000,
});

/**
 * Create a new user
 * @param {object} userData - User data object
 * @returns {Promise} API response
 */
const createUser = (userData = {}) => {
  return apiInstance.post("/api/users/new-user", userData, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

// /**
//  * Update user by ID
//  * @param {number|string} userId - User ID
//  * @param {object} userData - Updated user data
//  * @returns {Promise} API response
//  */
// const updateUser = (userId, userData = {}) => {
//   return apiInstance.put(`/api/users/${userId}`, userData, {
//     headers: {
//       "Content-Type": "application/json",
//     },
//   });
// };

const updateDealerPassword = (params = {}) => {
  return apiInstance.get("/update-dealer-password", {
    params: {
      ...params,
    },
  });
};

const getAgentReport = (params = {}) => {
  return apiInstance.get("/get-agent-report", {
    params: {
      ...params,
    },
  });
};

const sendEmail = (params = {}) => {
  return apiInstance.get("/send-email", {
    params: {
      ...params,
    },
  });
};

const startGenericFibreCsvImport = (params = {}) => {
  return apiInstance.get("/api/imports/trigger-csv-import", {
    params: {
      ...params,
    },
  });
};

/**
 * Enhanced getDealersForAdmin function with pagination, search, and filtering
 * @param {object} params - Request parameters
 * @param {string} params.dealer_id - Admin dealer ID
 * @param {string} params.access_token - Authentication token
 * @param {number} [params.page] - Page number (1-based, enables optimized mode)
 * @param {number} [params.limit] - Records per page (1-100, default: 100)
 * @param {string} [params.search] - Search term (name, email, phone, Z-ID)
 * @param {number} [params.status_filter] - Lifecycle status filter (0 = all)
 * @returns {Promise<object>} API response with dealers data
 */
const getDealersForAdmin = async params => {
  try {
    const queryParams = new URLSearchParams();

    // Add required parameters
    queryParams.append("dealer_id", params.dealer_id);
    queryParams.append("access_token", params.access_token);

    // Add optional parameters only if they have values
    if (params.page) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search && params.search.trim()) {
      queryParams.append("search", params.search.trim());
    }
    if (params.status_filter && params.status_filter !== 0) {
      queryParams.append("status_filter", params.status_filter.toString());
    }

    // const url = `${process.env.REACT_APP_API_BASE_URL || "http://localhost:4000"}/api/users/users-for-admin?${queryParams.toString()}`;

    const response = await fetch(
      `${url}api/users/users-for-admin?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error("Error fetching dealers for admin:", error);
    throw error;
  }
};

// Sending an sms can be done using the following example:
// import { useAuth } from " /src/contexts/AuthContext";
// const {authSession} = useAuth();
// import * as API from /src/api

// API.sendSMS({
// phone: '0832343508'
// phone: ['0832343508', '0832343509']
// message: 'this is my message'
// token: authSession.access_token
// })

const sendSMS = (params = {}) => {
  return apiInstance.get("/send-sms", {
    params: {
      ...params,
    },
  });
};

const updateEmailAndPhone = (params = {}) => {
  return apiInstance.get("/update-email-and-phone", {
    params: {
      ...params,
    },
  });
};

// API that handles the commissions import on the worker (server) after the file has been uploaded on supabase
const handleCommissionsImport = (params = {}) => {
  return apiInstance.get("/api/imports/handle-commissions-import", {
    params: {
      ...params,
    },
  });
};

// gets an array of contract objects from docuseal and lists it in the frontend.
const getAgentContracts = (params = {}) => {
  return apiInstance.get("/get-contract-submissions", {
    params: {
      ...params,
    },
  });
};

// Can be used to generate new contracts that will be sent to the agent. This function used docuseal as the supplier.
const generateNewContract = (params = {}) => {
  return apiInstance.get("/generate-new-contract", {
    params: {
      ...params,
    },
  });
};

// Can be used to get the download link of the contract that was generated by docuseal.
const getContractDownloadLink = (params = {}) => {
  return apiInstance.get("/get-contract-download-link", {
    params: {
      ...params,
    },
  });
};

/**
 * Creates a ClickUp ticket.
 *
 * @param {object} [params={}] - The parameters to be sent with the request.
 * @param {string} [params.list] - The id of the ClickUp list on which the ticket should be generated.
 * @param {string} [params.name] - The name of the ClickUp ticket.
 * @param {number} [params.desc] - The description of the ClickUp ticket.
 * @returns {Promise} - A promise that resolves to the response of the API call.
 */
const createClickupTicket = (params = {}) => {
  return apiInstance.get("/create-clickup-ticket", {
    params: {
      ...params,
    },
  });
};

const getLiveStructure = (params = {}) => {
  return apiInstance.get("/get-live-structure", {
    params: {
      ...params,
    },
  });
};
const getStructureOrderReport = (params = {}) => {
  return apiInstance.post("/get-structure-order-report", params, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

const getOrderReport = async params => {
  try {
    const {
      user_id,
      supplier_id,
      access_token,
      report,
      start_date,
      end_date,
      search,
      page,
      limit,
    } = params;

    // Build query parameters
    const queryParams = new URLSearchParams({
      user_id,
      supplier_id,
      access_token,
      report,
    });

    // Add optional parameters
    if (search && search.trim().length > 0) {
      queryParams.append("search", search.trim());
      if (page) queryParams.append("page", page.toString());
      if (limit) queryParams.append("limit", limit.toString());
    } else {
      // Only add date parameters if not searching
      if (start_date) queryParams.append("start_date", start_date);
      if (end_date) queryParams.append("end_date", end_date);
    }

    const response = await fetch(
      `${url}api/orders/get-order-report?${queryParams}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return {
        data: null,
        error: errorData.error || `HTTP error! status: ${response.status}`,
      };
    }

    const data = await response.json();
    return {
      data,
      error: null,
    };
  } catch (error) {
    console.error("API Error in getOrderReport:", error);
    return {
      data: null,
      error: error.message || "An unexpected error occurred",
    };
  }
};

const getUnmatchedOrders = (params = {}) => {
  return apiInstance.get("/get-unmatched-order", {
    params: {
      ...params,
    },
  });
};

const getOverrideOrders = (params = {}) => {
  return apiInstance.get("/get-override-order", {
    params: {
      ...params,
    },
  });
};

const rica = (params = {}) => {
  return apiInstance.get("/rica", {
    params: {
      ...params,
    },
  });
};

const getAvailableSupport = (params = {}) => {
  return apiInstance.get("api/support/available-support", {
    params: {
      ...params,
    },
  });
};

const getAvailableCredit = (params = {}) => {
  return apiInstance.get("api/support/available-credit", {
    params: {
      ...params,
    },
  });
};

export {
  createUser,
  updateDealerPassword,
  getAgentReport,
  sendEmail,
  sendSMS,
  updateEmailAndPhone,
  startGenericFibreCsvImport,
  handleCommissionsImport,
  getAgentContracts,
  generateNewContract,
  getContractDownloadLink,
  createClickupTicket,
  getLiveStructure,
  getStructureOrderReport,
  getOrderReport,
  getDealersForAdmin,
  getUnmatchedOrders,
  getOverrideOrders,
  rica,
  getAvailableSupport,
  getAvailableCredit,
};

// curl 'http://localhost:4000/get-structure-order-report' \
//   -H 'sec-ch-ua-platform: "macOS"' \
//   -H 'Referer: http://localhost:3000/' \
//   -H 'User-Agent: Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Safari/537.36' \
//   -H 'Accept: application/json, text/plain, */*' \
//   -H 'sec-ch-ua: "Chromium";v="136", "Brave";v="136", "Not.A/Brand";v="99"' \
//   -H 'Content-Type: application/json' \
//   -H 'sec-ch-ua-mobile: ?0' \
//   --data-raw '{"user_id":10020,"supplier_id":"4","start_date":"2025-06-01","end_date":"2025-06-30","is_admin":true}'
